/**
 * El tema: claro, oscuro o el que tenga el sistema.
 *
 * ---------------------------------------------------------------------------
 *  Cómo funciona
 * ---------------------------------------------------------------------------
 *
 * Se pone una clase en la etiqueta `<html>`:
 *   · `class="dark"`  → tema oscuro
 *   · `class="light"` → tema claro
 *
 * Y las variables de color de `index.css` hacen el resto. No hay que tocar ninguna
 * pantalla: las 14 cambian solas.
 *
 * ---------------------------------------------------------------------------
 *  Tres decisiones que importan
 * ---------------------------------------------------------------------------
 *
 * 1. **La preferencia se guarda en el navegador, no solo en el servidor.** Si
 *    esperáramos a que responda el servidor para saber si hay que pintar en claro,
 *    la pantalla saldría oscura un instante y luego cambiaría. Ese destello blanco
 *    (o negro) al abrir la aplicación de noche es de las cosas que peor se sienten.
 *    Por eso también hay un guion en `index.html` que la aplica antes de dibujar.
 *
 * 2. **«Sistema» no es un tercer color, es seguir al sistema.** Y sigue
 *    *en vivo*: si el móvil cambia a oscuro a las ocho de la tarde, la aplicación
 *    cambia con él, sin recargar.
 *
 * 3. **El tema se guarda también en el servidor**, para que la misma cuenta se vea
 *    igual en el computador y en el teléfono.
 */

import { useEffect, useState } from 'react';

/** Lo que puede elegir el usuario. */
export type ThemePreference = 'LIGHT' | 'DARK' | 'SYSTEM';

/** Lo que se está viendo de verdad. */
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'eldescuadre.theme';

/** El orden en que va cambiando el botón: claro → oscuro → sistema. */
export const THEME_CYCLE: readonly ThemePreference[] = ['DARK', 'LIGHT', 'SYSTEM'];

export const THEME_LABEL: Record<ThemePreference, string> = {
  LIGHT: 'Claro',
  DARK: 'Oscuro',
  SYSTEM: 'Sistema',
};

/** ¿El sistema está en oscuro? */
function systemPrefersDark(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return true;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/**
 * El tema guardado en el navegador.
 *
 * Se lee de forma defensiva: si el navegador tiene el almacenamiento bloqueado (pasa
 * en modo privado y en algunos navegadores dentro de aplicaciones), no se puede
 * dejar que la aplicación no arranque por no poder leer una preferencia de color.
 */
export function readStoredTheme(): ThemePreference {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'LIGHT' || stored === 'DARK' || stored === 'SYSTEM') return stored;
  } catch {
    // Sin almacenamiento: se usa el tema del sistema.
  }
  return 'SYSTEM';
}

export function storeTheme(theme: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // No poder recordarlo no debe impedir cambiarlo ahora.
  }
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === 'SYSTEM') return systemPrefersDark() ? 'dark' : 'light';
  return preference === 'LIGHT' ? 'light' : 'dark';
}

/**
 * Aplica el tema a la etiqueta `<html>`.
 *
 * Se ponen **las dos**: `light`/`dark` para nuestras variables, y `dark` además para
 * que las variantes `dark:` de Tailwind sigan funcionando si alguna se usa suelta.
 */
export function applyTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  root.classList.toggle('light', resolved === 'light');
  root.classList.toggle('dark', resolved === 'dark');

  // El color de la barra del navegador en el móvil, que si no desentona.
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', resolved === 'light' ? '#f6f8fc' : '#071224');
}

/**
 * El tema, con su preferencia y su botón de cambiar.
 *
 * @param remoteTheme El tema guardado en el servidor, cuando llega.
 */
export function useTheme(remoteTheme?: ThemePreference | undefined): {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (theme: ThemePreference) => void;
  cycle: () => ThemePreference;
} {
  const [preference, setPreferenceState] = useState<ThemePreference>(() =>
    remoteTheme ?? readStoredTheme(),
  );

  // El tema del servidor manda cuando llega: es la misma cuenta en otro dispositivo.
  useEffect(() => {
    if (remoteTheme) setPreferenceState(remoteTheme);
  }, [remoteTheme]);

  const resolved = resolveTheme(preference);

  // Se aplica al dibujar, y se escucha al sistema si toca seguirle.
  useEffect(() => {
    applyTheme(resolved);

    if (preference !== 'SYSTEM' || typeof window === 'undefined' || !window.matchMedia) return;

    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (): void => applyTheme(systemPrefersDark() ? 'dark' : 'light');

    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [preference, resolved]);

  return {
    preference,
    resolved,
    setPreference: (theme) => {
      setPreferenceState(theme);
      storeTheme(theme);
    },
    cycle: () => {
      const next = THEME_CYCLE[(THEME_CYCLE.indexOf(preference) + 1) % THEME_CYCLE.length]!;
      setPreferenceState(next);
      storeTheme(next);
      return next;
    },
  };
}
